import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Divider,
    Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { FuneralHome } from "../listing-page.types";
import { useNavigate } from "react-router-dom";
import ProviderCardContactModal from "../../../common/components/provider-card-contactus-modal/ProviderCardContactModal";
import "./compare-screen.scss";

interface CompareScreenProps {
    compareList: FuneralHome[];
    onRemove: (id: number) => void;
    onBack: () => void;
}

const CompareScreen: React.FC<CompareScreenProps> = ({
    compareList,
    onRemove,
    onBack,
}) => {
    const navigate = useNavigate();
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [selectedProviderForContact, setSelectedProviderForContact] = useState<FuneralHome | null>(null);

    const handleOpenContact = (provider: FuneralHome) => {
        setSelectedProviderForContact(provider);
        setContactModalOpen(true);
    };

    const handleCloseContact = () => {
        setContactModalOpen(false);
        setSelectedProviderForContact(null);
    };

    const getImageUrl = (item: FuneralHome) => {
        const fileInfo = item.fileInfo || (item as any).file_info;
        if (!fileInfo || !Array.isArray(fileInfo)) return "";

        const image = fileInfo.find((f: any) => {
            const mediaType = (f.mediaType || f.media_type)?.toLowerCase();
            return mediaType === "image" && (f.url || f.path);
        });

        const url = (image as any)?.url || (image as any)?.path || (fileInfo[0] as any)?.url || (fileInfo[0] as any)?.path || "";
        return url;
    };

    return (
        <Box className="compare-compare-screen-container">
            {/* Header / Breadcrumbs */}
            <Box className="compare-compare-header">
                <Typography variant="body1" className="compare-breadcrumb-text">
                    <Box component="span" className="compare-clickable-breadcrumb" onClick={() => navigate("/home")}>Home</Box> / <Box component="span" className="compare-clickable-breadcrumb" onClick={onBack}>Funeral Homes</Box> / <Box component="span" className="compare-active-page">Compare Screen</Box>
                </Typography>
            </Box>

            <Box className="compare-compare-content">
                {compareList.length === 0 ? (
                    <Box className="compare-empty-message">
                        <Typography variant="h6" color="text.secondary">
                            Your comparison list is empty.
                        </Typography>
                    </Box>
                ) : (
                    compareList.map((provider) => (
                        <Paper key={provider.id} className="compare-compare-card">
                            <IconButton
                                onClick={() => onRemove(provider.id)}
                                className="compare-remove-btn"
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>

                            <Box className="compare-card-image-wrapper">
                                <img
                                    src={getImageUrl(provider)}
                                    alt={provider.businessName}
                                    onError={(e: any) => {
                                        e.target.src = "https://via.placeholder.com/400x200?text=No+Image+Available";
                                    }}
                                />
                            </Box>

                            <Box className="compare-card-details">
                                <Typography variant="h6" className="compare-provider-name">
                                    {provider.businessName}
                                </Typography>
                                <Typography variant="body2" className="compare-provider-location">
                                    {(provider.address as any)?.city} , {(provider.address as any)?.state}
                                </Typography>

                                <Divider className="compare-divider" />

                                <Box className="compare-rating-box">
                                    <StarRoundedIcon className="compare-star-icon" />
                                    <Typography variant="body2" className="compare-rating-text">
                                        {provider.avgRating?.toFixed(1) || "0.0"}
                                    </Typography>
                                </Box>

                                <Divider className="compare-divider" />

                                <Typography variant="h4" className="compare-provider-price">
                                    ${(provider.startingPrice || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>

                                <Divider className="compare-divider" />

                                <Typography variant="body2" className="compare-provider-description">
                                    {provider.description}
                                </Typography>

                                <Divider className="compare-divider" />

                                <Box className="compare-services-table">
                                    <Box className="compare-services-header">
                                        <Typography variant="body2" className="compare-header-title">
                                            Funeral Home
                                        </Typography>
                                    </Box>
                                    <Box className="compare-services-list">
                                        {(provider.services as any[] || []).length > 0 ? (
                                            (provider.services as any[]).map((service, idx) => (
                                                <Box key={idx} className="compare-service-item">
                                                    <Box className="compare-service-info">
                                                        <Typography variant="body2" className="compare-service-name">
                                                            {service.name}
                                                        </Typography>
                                                        <Typography variant="caption" className="compare-service-desc">
                                                            Professional services and staff
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" className="compare-service-price">
                                                        ${(
                                                            (service.partnerServiceItems && Array.isArray(service.partnerServiceItems) && service.partnerServiceItems.length > 0)
                                                                ? service.partnerServiceItems.reduce((acc: number, item: any) => acc + (parseFloat(item.price) || 0), 0)
                                                                : (service.startingPrice || 0)
                                                        ).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Box className="compare-no-services">
                                                <Typography variant="caption">No detailed services available</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 'auto', width: '100%' }}>
                                    <Divider className="compare-divider" />

                                    <Box className="compare-actions-box" sx={{ mt: 0 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => navigate(`details/${provider.id}`)}
                                            className="compare-action-btn compare-view-details"
                                        >
                                            View Details
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleOpenContact(provider)}
                                            className="compare-action-btn compare-contact-info"
                                        >
                                            Contact Info
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    ))
                )}
            </Box>

            {selectedProviderForContact && (
                <ProviderCardContactModal
                    open={contactModalOpen}
                    onClose={handleCloseContact}
                    title={selectedProviderForContact.businessName}
                    address={selectedProviderForContact.address}
                    latitude={Number((selectedProviderForContact.contactInfo as any)?.latitude)}
                    longitude={Number((selectedProviderForContact.contactInfo as any)?.longitude)}
                    phoneNumber={selectedProviderForContact.phoneNumber}
                    email={selectedProviderForContact.email}
                    contactInfo={selectedProviderForContact.contactInfo}
                />
            )}
        </Box>
    );
};

export default CompareScreen;
